/**
 * Documents on screen: the markdown shown word for word (headings and paragraphs), its sha256 (what the
 * signature records), Read aloud with the device's own voice (Web Speech API — no network), and printing
 * through a portal that the print stylesheet alone shows.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** sha256 hex of the exact text shown (the server checks it against its copy). */
export async function sha256Text(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  // 1.8.12: crypto.subtle is missing on a page that is not a secure context and in some older or locked-down browsers — there the press
  // used to do nothing at all. The same hash is worked out in plain script instead, so the signature still goes through.
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    try {
      const buf = await crypto.subtle.digest('SHA-256', bytes);
      return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch {
      /* fall through to the plain version */
    }
  }
  return sha256Plain(bytes);
}

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

/** SHA-256 in plain script (FIPS 180-4) — only the fallback; tested against the platform's own. */
export function sha256Plain(data: Uint8Array): string {
  const h = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]);
  const padded = new Uint8Array(Math.ceil((data.length + 9) / 64) * 64);
  padded.set(data);
  padded[data.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor((data.length * 8) / 0x100000000));
  view.setUint32(padded.length - 4, (data.length * 8) >>> 0);
  const w = new Uint32Array(64);
  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));
  for (let off = 0; off < padded.length; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15]!, 7) ^ rotr(w[i - 15]!, 18) ^ (w[i - 15]! >>> 3);
      const s1 = rotr(w[i - 2]!, 17) ^ rotr(w[i - 2]!, 19) ^ (w[i - 2]! >>> 10);
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = [h[0]!, h[1]!, h[2]!, h[3]!, h[4]!, h[5]!, h[6]!, h[7]!];
    for (let i = 0; i < 64; i++) {
      const t1 = (hh + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K[i]! + w[i]!) >>> 0;
      const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    h[0] = (h[0]! + a) >>> 0;
    h[1] = (h[1]! + b) >>> 0;
    h[2] = (h[2]! + c) >>> 0;
    h[3] = (h[3]! + d) >>> 0;
    h[4] = (h[4]! + e) >>> 0;
    h[5] = (h[5]! + f) >>> 0;
    h[6] = (h[6]! + g) >>> 0;
    h[7] = (h[7]! + hh) >>> 0;
  }
  return [...h].map((x) => x.toString(16).padStart(8, '0')).join('');
}

/** Headings, paragraphs, `- ` and `1. ` lists; `**bold**` kept; nothing else interpreted. */
export function MarkdownText({ text, headingLevel = 3 }: { text: string; headingLevel?: 2 | 3 }) {
  const blocks = text.replace(/\r\n/g, '\n').split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  const H1 = headingLevel === 2 ? 'h2' : 'h3';
  const H2 = headingLevel === 2 ? 'h3' : 'h4';
  const inline = (s: string): ReactNode[] => s.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (part.startsWith('**') && part.endsWith('**') ? <b key={i}>{part.slice(2, -2)}</b> : part));
  return (
    <>
      {blocks.map((b, i) => {
        if (b.startsWith('# ')) return <H1 key={i}>{b.slice(2)}</H1>;
        if (b.startsWith('## ')) return <H2 key={i}>{b.slice(3)}</H2>;
        if (/^- /m.test(b) && b.split('\n').every((l) => l.startsWith('- '))) {
          return (
            <ul key={i}>
              {b.split('\n').map((l, j) => (
                <li key={j}>{inline(l.slice(2))}</li>
              ))}
            </ul>
          );
        }
        if (b.split('\n').every((l) => /^\d+\. /.test(l))) {
          return (
            <ol key={i}>
              {b.split('\n').map((l, j) => (
                <li key={j}>{inline(l.replace(/^\d+\. /, ''))}</li>
              ))}
            </ol>
          );
        }
        return (
          <p key={i}>
            {b.split('\n').map((l, j, all) => (
              <span key={j}>
                {inline(l)}
                {j < all.length - 1 ? <br /> : null}
              </span>
            ))}
          </p>
        );
      })}
    </>
  );
}

/** Plain text for speech: headings become sentences, markup removed. */
export function speakable(md: string): string {
  return md
    .replace(/^#+\s*/gm, '')
    .replace(/^(- |\d+\. )/gm, '')
    .replace(/\*\*/g, '')
    .replace(/^>\s*/gm, '')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

export function speechAvailable(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined';
}

/** Read aloud: one utterance per paragraph (long texts stall in some browsers otherwise). */
export function useReadAloud(lang: 'en' | 'es') {
  const [speaking, setSpeaking] = useState(false);
  const cancelled = useRef(false);
  useEffect(() => () => {
    if (speechAvailable()) window.speechSynthesis.cancel();
  }, []);
  const stop = () => {
    cancelled.current = true;
    if (speechAvailable()) window.speechSynthesis.cancel();
    setSpeaking(false);
  };
  const start = (text: string) => {
    if (!speechAvailable()) return false;
    window.speechSynthesis.cancel();
    cancelled.current = false;
    const parts = speakable(text).split('\n').filter(Boolean);
    const voices = window.speechSynthesis.getVoices();
    const voice = voices.find((v) => v.lang.toLowerCase().startsWith(lang)) ?? null;
    parts.forEach((p, i) => {
      const u = new SpeechSynthesisUtterance(p);
      u.lang = lang === 'es' ? 'es-US' : 'en-US';
      if (voice) u.voice = voice;
      u.rate = 0.95;
      if (i === parts.length - 1) u.onend = () => setSpeaking(false);
      window.speechSynthesis.speak(u);
    });
    setSpeaking(true);
    return true;
  };
  return { speaking, start, stop, available: speechAvailable() };
}

/**
 * Print: the content goes into #print-root (a portal), the print stylesheet hides everything else, and the
 * browser's own print dialog opens. `data-printing` stays on <html> until the dialog closes.
 */
/**
 * 1.5.11 §1: what the app prints through the browser (the packet, a summary, a guide) carries the same letterhead
 * as the PDFs it writes — the logo, the name and the Selis Manor address, the rule in the brand purple — and a line
 * at the foot. The page numbers are the browser's own.
 */
export const PRINT_ORG = 'Associated Blind Foundation';
export const PRINT_ADDRESS = 'Selis Manor · 135 West 23rd Street, New York, NY 10011';
export const PRINT_FOOT = 'ABF Scheduler · Associated Blind Foundation · Selis Manor';

export function PrintLetterhead() {
  return (
    <div className="print-letterhead" data-testid="print-letterhead">
      <img src="/brand/logo-light.png" alt={PRINT_ORG} />
      <div>
        <b>{PRINT_ORG}</b>
        <span>{PRINT_ADDRESS}</span>
      </div>
    </div>
  );
}

export function PrintArea({ active, children, onDone }: { active: boolean; children: ReactNode; onDone: () => void }) {
  const [node, setNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    let el = document.getElementById('print-root');
    if (!el) {
      el = document.createElement('div');
      el.id = 'print-root';
      document.body.appendChild(el);
    }
    setNode(el);
  }, []);
  useEffect(() => {
    if (!active || !node) return;
    document.documentElement.dataset.printing = 'true';
    const after = () => {
      delete document.documentElement.dataset.printing;
      window.removeEventListener('afterprint', after);
      onDone();
    };
    window.addEventListener('afterprint', after);
    const timer = window.setTimeout(() => window.print(), 50);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('afterprint', after);
    };
  }, [active, node, onDone]);
  return node && active
    ? createPortal(
        <div className="print-doc">
          <PrintLetterhead />
          {children}
          <p className="print-foot" data-testid="print-foot">
            {PRINT_FOOT}
          </p>
        </div>,
        node,
      )
    : null;
}

/**
 * Print what is on screen (the close-out record): a copy of `node` goes into #print-root and the browser's
 * print dialog opens; the copy is cleared when the dialog closes. Buttons are left out of the copy.
 */
export function printNode(node: HTMLElement, title: string): void {
  let root = document.getElementById('print-root');
  if (!root) {
    root = document.createElement('div');
    root.id = 'print-root';
    document.body.appendChild(root);
  }
  const doc = document.createElement('div');
  doc.className = 'print-doc';
  const h = document.createElement('h1');
  h.textContent = title;
  doc.appendChild(h);
  const copy = node.cloneNode(true) as HTMLElement;
  copy.querySelectorAll('button, [data-no-print]').forEach((b) => b.remove());
  copy.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
  doc.appendChild(copy);
  root.replaceChildren(doc);
  document.documentElement.dataset.printing = 'true';
  const after = () => {
    delete document.documentElement.dataset.printing;
    root?.replaceChildren();
    window.removeEventListener('afterprint', after);
  };
  window.addEventListener('afterprint', after);
  window.setTimeout(() => window.print(), 50);
}
